import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Fade, Zoom, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';

// ============================================================
// 🦏 EASTER EGG — Welcome screen doar pentru Jessica
// ============================================================

const JESSICA_EMAIL = 'jessicaradu@yahoo.com';
const JESSICA_BIRTHDAY_MONTH = 8; // August
const JESSICA_BIRTHDAY_DAY = 13;

function getNextBirthday() {
    const now = new Date();
    const year = now.getFullYear();
    let birthday = new Date(year, JESSICA_BIRTHDAY_MONTH - 1, JESSICA_BIRTHDAY_DAY);

    if (now.getDate() === JESSICA_BIRTHDAY_DAY && (now.getMonth() + 1) === JESSICA_BIRTHDAY_MONTH) {
        return { isBirthday: true, target: birthday };
    }

    if (now > birthday) {
        birthday = new Date(year + 1, JESSICA_BIRTHDAY_MONTH - 1, JESSICA_BIRTHDAY_DAY);
    }

    return { isBirthday: false, target: birthday };
}

function getTimeLeft(target) {
    const now = new Date();
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
}

// ── Animations ──
const bounce = keyframes`
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-30px) rotate(-5deg); }
    50% { transform: translateY(0) rotate(0deg); }
    75% { transform: translateY(-15px) rotate(5deg); }
`;

const dust = keyframes`
    0% { opacity: 0; transform: scale(0.5) translateX(0); }
    30% { opacity: 0.6; }
    100% { opacity: 0; transform: scale(1.5) translateX(40px); }
`;

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33% { transform: translateY(-8px) rotate(2deg); }
    66% { transform: translateY(4px) rotate(-1deg); }
`;

const stomp = keyframes`
    0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
    50% { transform: scale(1.15) rotate(3deg); opacity: 1; }
    70% { transform: scale(0.95) rotate(-1deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const sparkle = keyframes`
    0%, 100% { opacity: 0; transform: scale(0); }
    50% { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
`;

const tickPop = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
`;

const candleFlicker = keyframes`
    0%, 100% { transform: translateX(0) scale(1); opacity: 1; }
    25% { transform: translateX(-1px) scale(1.1); opacity: 0.8; }
    50% { transform: translateX(1px) scale(0.9); opacity: 1; }
    75% { transform: translateX(-0.5px) scale(1.05); opacity: 0.9; }
`;

const confettiFall = keyframes`
    0% { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

// ── Styled Components ──
const Overlay = styled(Box)({
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #0f3460 60%, #1a1a2e 100%)',
    overflow: 'hidden',
    padding: '20px',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'radial-gradient(circle at 30% 40%, rgba(233,196,106,0.08) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(244,162,97,0.06) 0%, transparent 40%)',
        pointerEvents: 'none',
    },
});

const RhinoEmoji = styled(Box)(({ theme }) => ({
    fontSize: '100px',
    lineHeight: 1,
    animation: `${stomp} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))',
    userSelect: 'none',
    position: 'relative',
    marginBottom: '20px',
    [theme.breakpoints.down('sm')]: {
        fontSize: '70px',
        marginBottom: '12px',
    },
}));

const BouncingRhino = styled(Box)({
    animation: `${bounce} 2s ease-in-out infinite`,
    animationDelay: '0.8s',
    display: 'inline-block',
});

const DustCloud = styled(Box)(({ delay = 0, left = 0 }) => ({
    position: 'absolute',
    bottom: '-10px',
    left: `${left}px`,
    width: '20px', height: '20px',
    borderRadius: '50%',
    background: 'rgba(233, 196, 106, 0.3)',
    animation: `${dust} 1s ease-out forwards`,
    animationDelay: `${0.6 + delay}s`,
    opacity: 0,
}));

const Sparkle = styled(Box)(({ top, left, delay, size = 8 }) => ({
    position: 'absolute',
    top: `${top}%`, left: `${left}%`,
    width: `${size}px`, height: `${size}px`,
    borderRadius: '50%',
    background: 'rgba(233, 196, 106, 0.8)',
    animation: `${sparkle} 2s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    boxShadow: '0 0 10px rgba(233, 196, 106, 0.4)',
}));

const GradientText = styled(Typography)({
    background: 'linear-gradient(90deg, #e9c46a, #f4a261, #e76f51, #f4a261, #e9c46a)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: `${shimmer} 3s linear infinite`,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    textAlign: 'center',
    fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
});

const BirthdayText = styled(Typography)(({ theme }) => ({
    background: 'linear-gradient(90deg, #ff6b6b, #ffd93d, #ff6b6b, #ffd93d)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: `${shimmer} 2s linear infinite, ${pulse} 1.5s ease-in-out infinite`,
    fontWeight: 800,
    fontSize: '1.3rem',
    marginTop: '16px',
    maxWidth: '500px',
    lineHeight: 1.6,
    fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
        marginTop: '12px',
        maxWidth: '90vw',
    },
}));

const Confetti = styled(Box)(({ left, delay, color }) => ({
    position: 'absolute',
    top: '-5%',
    left: `${left}%`,
    width: '10px', height: '10px',
    borderRadius: '2px',
    background: color,
    animation: `${confettiFall} 3s ease-in infinite`,
    animationDelay: `${delay}s`,
}));

const FootprintTrail = styled(Box)({
    position: 'absolute',
    bottom: '10%',
    left: '10%', right: '10%',
    display: 'flex',
    justifyContent: 'space-around',
    opacity: 0.08,
    fontSize: '24px',
    pointerEvents: 'none',
});

// ── Timer Styled Components ──
const TimerContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        gap: '6px',
        marginTop: '16px',
    },
}));

const TimerBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '70px',
    [theme.breakpoints.down('sm')]: {
        minWidth: '48px',
    },
}));

const TimerNumber = styled(Box)(({ theme }) => ({
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(233, 196, 106, 0.2)',
    borderRadius: '12px',
    padding: '12px 16px',
    minWidth: '64px',
    textAlign: 'center',
    fontSize: '2rem',
    fontWeight: 800,
    color: '#e9c46a',
    fontFamily: '"Segoe UI", monospace',
    letterSpacing: '0.05em',
    animation: `${tickPop} 1s ease-in-out infinite`,
    boxShadow: '0 4px 20px rgba(233, 196, 106, 0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
    [theme.breakpoints.down('sm')]: {
        padding: '8px 10px',
        minWidth: '48px',
        fontSize: '1.4rem',
        borderRadius: '8px',
    },
}));

const TimerLabel = styled(Typography)(({ theme }) => ({
    color: 'rgba(233, 196, 106, 0.5)',
    fontSize: '0.65rem',
    fontWeight: 500,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    marginTop: '8px',
    fontFamily: '"Segoe UI", sans-serif',
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.55rem',
        marginTop: '4px',
    },
}));

const TimerSeparator = styled(Box)(({ theme }) => ({
    fontSize: '1.8rem',
    color: 'rgba(233, 196, 106, 0.3)',
    fontWeight: 300,
    alignSelf: 'flex-start',
    paddingTop: '12px',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.2rem',
        paddingTop: '8px',
    },
}));

const CakeContainer = styled(Box)(({ theme }) => ({
    fontSize: '48px',
    marginLeft: '20px',
    animation: `${candleFlicker} 2s ease-in-out infinite`,
    filter: 'drop-shadow(0 4px 15px rgba(255, 200, 50, 0.3))',
    alignSelf: 'center',
    [theme.breakpoints.down('sm')]: {
        fontSize: '32px',
        marginLeft: '8px',
    },
}));

export default function RhinoWelcome({ user, onComplete }) {
    const [visible, setVisible] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    const isJessica = user?.email?.toLowerCase() === JESSICA_EMAIL.toLowerCase();
    const { isBirthday, target } = getNextBirthday();

    const stableOnComplete = useCallback(() => {
        onComplete?.();
    }, [onComplete]);

    useEffect(() => {
        if (!isJessica || isBirthday) return;

        setTimeLeft(getTimeLeft(target));
        const interval = setInterval(() => {
            setTimeLeft(getTimeLeft(target));
        }, 1000);

        return () => clearInterval(interval);
    }, [isJessica, isBirthday, target]);

    useEffect(() => {
        if (!isJessica) {
            stableOnComplete();
            return;
        }

        setVisible(true);

        const duration = isBirthday ? 8000 : 6000;
        const timer = setTimeout(() => {
            setFadeOut(true);
            setTimeout(() => stableOnComplete(), 500);
        }, duration);

        return () => clearTimeout(timer);
    }, [isJessica, isBirthday, stableOnComplete]);

    if (!isJessica || !visible) return null;

    const handleClick = () => {
        setFadeOut(true);
        setTimeout(() => stableOnComplete(), 400);
    };

    const pad = (n) => String(n).padStart(2, '0');

    const confettiColors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#e76f51', '#a855f7', '#ec4899', '#06b6d4'];

    return (
        <Fade in={!fadeOut} timeout={fadeOut ? 400 : 600}>
            <Overlay onClick={handleClick}>
                {/* Sparkles */}
                <Sparkle top={15} left={20} delay={1.2} size={6} />
                <Sparkle top={25} left={75} delay={0.5} size={10} />
                <Sparkle top={60} left={15} delay={1.8} size={8} />
                <Sparkle top={70} left={80} delay={0.8} size={5} />
                <Sparkle top={40} left={90} delay={2.0} size={7} />
                <Sparkle top={80} left={45} delay={1.5} size={6} />
                <Sparkle top={10} left={50} delay={2.2} size={9} />

                {isBirthday && confettiColors.map((color, i) => (
                    <Confetti key={i} left={5 + i * 12} delay={i * 0.4} color={color} />
                ))}

                <RhinoEmoji>
                    <BouncingRhino>{isBirthday ? '🦢' : '🦏'}</BouncingRhino>
                    <DustCloud delay={0} left={-15} />
                    <DustCloud delay={0.1} left={5} />
                    <DustCloud delay={0.2} left={25} />
                    <DustCloud delay={0.15} left={80} />
                    <DustCloud delay={0.25} left={100} />
                </RhinoEmoji>

                <Zoom in={!fadeOut} style={{ transitionDelay: '0.4s' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <GradientText
                            variant="h3"
                            sx={{
                                mb: 0,
                                fontSize: { xs: '1.6rem', sm: '2.5rem', md: '3rem' },
                            }}
                        >
                            {isBirthday ? 'Happy Birthday, Barbie!' : 'Welcome back, little Rhino!'}
                        </GradientText>

                        {isBirthday ? (
                            <BirthdayText>
                                Don't forget how special you are and don't let anyone steal your light ✨🦢
                            </BirthdayText>
                        ) : (
                            <TimerContainer>
                                <TimerBlock>
                                    <TimerNumber>{pad(timeLeft.days)}</TimerNumber>
                                    <TimerLabel>zile</TimerLabel>
                                </TimerBlock>

                                <TimerSeparator>:</TimerSeparator>

                                <TimerBlock>
                                    <TimerNumber>{pad(timeLeft.hours)}</TimerNumber>
                                    <TimerLabel>ore</TimerLabel>
                                </TimerBlock>

                                <TimerSeparator>:</TimerSeparator>

                                <TimerBlock>
                                    <TimerNumber>{pad(timeLeft.minutes)}</TimerNumber>
                                    <TimerLabel>minute</TimerLabel>
                                </TimerBlock>

                                <TimerSeparator>:</TimerSeparator>

                                <TimerBlock>
                                    <TimerNumber>{pad(timeLeft.seconds)}</TimerNumber>
                                    <TimerLabel>secunde</TimerLabel>
                                </TimerBlock>

                                <CakeContainer>🎂</CakeContainer>
                            </TimerContainer>
                        )}
                    </Box>
                </Zoom>

                <FootprintTrail>
                    {['🐾', '🐾', '🐾', '🐾', '🐾'].map((fp, i) => (
                        <Box
                            key={i}
                            sx={{
                                transform: `rotate(${-30 + i * 15}deg)`,
                                animation: `${float} ${3 + i * 0.5}s ease-in-out infinite`,
                                animationDelay: `${i * 0.3}s`,
                            }}
                        >
                            {fp}
                        </Box>
                    ))}
                </FootprintTrail>
            </Overlay>
        </Fade>
    );
}